<template>
  <div class="email-not-received mt-3">
    <p class="subtitle">Essa é mais uma medida de segurança para garantir que você é a única pessoa acessando sua conta Mercafacil.</p>
    <p class="email-text">
      Disparamos um e-mail com um código de acesso para <strong>{{ username }}</strong
      >, para autorizar esse acesso, basta inserir o código recebido abaixo:
    </p>

    <pin-input ref="pinInput" :error="pinError" @send-code="handleUserPin($event)" />

    <turnstile-widget
      ref="turnstileRef"
      class="mt-3"
      :sitekey="turnstileSitekey"
      @error="handleTurnstileError"
      @reset="handleTurnstileReset"
      @verified="handleTurnstileVerified"
    />

    <v-btn block class="btn-submit-form mt-3" color="primary" :disabled="loading || !canSubmit" :loading="loading" @click="handleSubmit">
      Verificar código
    </v-btn>
    <template v-if="senderType === 'EMAIL' && !invalidSender">
      <div v-if="step === 'pin-input' || step === 'dont-received-email'">
        <div class="text-center mt-2">
          <span class="py-2 px-4 link" :class="disabledTimer ? 'disabled-timer' : ''" @click="resendTwoFactorCode">Redisparar e-mail {{ secondsText }}</span>
        </div>
        <div v-if="step !== 'dont-received-email'" class="text-center mt-2 mb-0">
          <span class="py-2 px-4 link" @click="dontReceivedEmail">Não recebi meu e-mail</span>
        </div>
        <div v-else-if="step === 'dont-received-email' && phone && phoneConfirmed" class="d-flex justify-center">
          <span class="py-2 px-4 link" @click="trySms">Tentar por SMS</span>
        </div>
      </div>
    </template>
    <template v-else-if="!invalidSender">
      <div>
        <div v-if="step !== 'dont-received-sms'" class="text-center mt-2">
          <span class="py-2 px-4 link" :class="disabledTimer ? 'disabled-timer' : ''" @click="resendTwoFactorCode">Redisparar SMS {{ secondsText }}</span>
        </div>
        <div v-if="step !== 'dont-received-sms'" class="text-center mt-2 mb-0">
          <span
            class="py-2 px-4 link"
            @click="
              step = 'dont-received-sms'
              invalidSender = true
            "
            >Não recebi meu SMS</span
          >
        </div>
      </div>
    </template>

    <div v-if="invalidSender" class="d-flex justify-center">
      <div>
        <p class="mb-4">
          Entre em contato com o administrador da sua plataforma para conferir seus dados e solicitar a atualização do seu acesso pela plataforma Mercafacil.
        </p>
        <p>Em último caso entre em contato com: atendimeto@mercafacil.com</p>
        <div class="link-container mt-6 text-center">
          <span
            class="link"
            @click="
              step = 'pin-input'
              invalidSender = false
            "
            >Recebi meu e-mail</span
          >
        </div>
      </div>
    </div>

    <back-to-login @go-back="goBack" />
  </div>
</template>

<script>
import config from '@/config'
import { pinInput } from '@/modules/login/mixins/pin-input'
import { MUTATION_RESEND_TWO_FACTOR_CODE } from '@/modules/login/graphql'

export default {
  name: 'TwoFactorAuthentication',
  components: {
    PinInput: () => import('@/components/PinInput.vue'),
    BackToLogin: () => import('@/components/BackToLogin.vue'),
    TurnstileWidget: () => import('@/components/TurnstileWidget.vue'),
  },
  mixins: [pinInput],
  props: {
    token: {
      type: String,
      default: '',
    },
    pinErr: {
      type: String,
      default: '',
    },
    username: {
      type: String,
      default: '',
    },
    hiddenPhone: {
      type: String,
      default: '',
    },
    phoneConfirmed: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    step: 'pin-input',
    turnstileSitekey: config.cloudflare.turnstile.sitekey,
    code: '',
    preventSendCode: false,
    turnstileToken: '',
    senderType: 'EMAIL',
    time: null,
    invalidSender: false,
  }),
  computed: {
    canSubmit() {
      return this.turnstileToken && this.code.length === 6 && !this.preventSendCode
    },
    dontVerifyAgain: {
      get() {
        return this.dontVerifyDeviceAgain
      },
      set(value) {
        this.$emit('update:dontVerifyDeviceAgain', value)
      },
    },
  },
  watch: {
    pinErr(value) {
      this.pinError = value
      if (value) this.code = ''
    },
    hiddenPhone(value) {
      this.phone = value
    },
  },
  created() {
    this.phone = this.hiddenPhone
  },
  methods: {
    validateTurnstile() {
      if (this.turnstileToken) return true
      this.$refs.turnstileRef.toggleError(true)
    },
    enabledResend() {
      this.$refs.turnstileRef?.reset()
    },
    dontReceivedEmail() {
      this.step = 'dont-received-email'
      if (!this.phoneConfirmed) {
        this.invalidSender = true
      }
    },
    async resendTwoFactorCode() {
      if (this.disabledTimer) return
      if (!this.validateTurnstile()) return
      this.pinError = ''
      this.loading = true

      try {
        this.$refs.pinInput?.reset()
        await this.$apollo.mutate({
          mutation: MUTATION_RESEND_TWO_FACTOR_CODE,
          fetchPolicy: 'no-cache',
          variables: {
            token: this.token,
            senderType: this.senderType,
          },
          context: {
            headers: {
              fingerprint: this.fingerprint,
              'turnstile-token': this.turnstileToken,
            },
          },
        })

        this.startTimer()
      } catch (error) {
        console.log('resendTwoFactorCode', error)
        this.$emit('error', error)
      } finally {
        this.$refs.turnstileRef.reset()
        this.loading = false
        this.turnstileToken = ''
      }
    },
    goBack() {
      this.$emit('go-back')
    },
    async handleTurnstileVerified(token) {
      this.turnstileToken = token
    },
    async handleTurnstileReset() {
      this.turnstileToken = ''
    },
    async handleTurnstileError(errorCode) {
      console.log('handleTurnstileError', errorCode)
    },
    handleUserPin(code) {
      this.code = code
    },
    handleSubmit() {
      this.$emit('remove-error')
      if (!this.turnstileToken) return
      if (this.code.length < 6) return
      if (this.preventSendCode) return
      this.preventSendCode = true
      this.time = setTimeout(() => {
        this.$emit('send-code', { code: this.code, turnstileToken: this.turnstileToken })
        this.preventSendCode = false
      }, 300)
    },
    trySms() {
      this.phone = this.hiddenPhone
      clearTimeout(this.time)
      this.step = 'try-sms'
      this.senderType = 'SMS'
      this.resendTwoFactorCode()
    },
  },
}
</script>

<style lang="scss" scoped>
.email-not-received {
  & p {
    font-size: 16px;
    line-height: 120%;
    text-align: center;
    font-weight: 400;
  }
  & .subtitle {
    color: #4b525a;
  }

  & strong {
    color: #00b395;
  }

  & .email-text {
    color: #6b7580;
  }

  & .link-container {
    text-align: center;
  }
  & ::v-deep label {
    color: #4b525a;
    font-size: 14px;
  }
}
</style>
